// MJOS-132 iteration 4 — Avatars Lab. Three hero panels side-by-side.
//
// Left:   TalkingHead (met4citizen, MIT) + RPM GLB + HeadTTS Kokoro.
// Middle: VRoid anime (three-vrm + CC0 fem_vroid) + HeadTTS Kokoro.
// Right:  RobotExpressive (CC0 three.js core) + HeadTTS Kokoro amplitude.

import { useCallback, useEffect, useRef, useState } from "react";
import { TalkingHead } from "@met4citizen/talkinghead";
import { VRoidPanel } from "./VRoidPanel";
import { RobotPanel } from "./RobotPanel";
import { LipsyncEn } from "@met4citizen/talkinghead/modules/lipsync-en.mjs";
import { HeadTTS } from "@met4citizen/headtts";
import {
  Sparkles,
  Loader2,
  Heart,
  Smile,
  Frown,
  Angry,
  ShieldAlert,
  Volume2,
  ExternalLink,
  Lock,
  Cpu,
} from "lucide-react";

const AVATARS = [
  { id: "brunette", label: "Brunette", body: "F" as const, url: "https://raw.githubusercontent.com/met4citizen/TalkingHead/main/avatars/brunette.glb" },
  { id: "brunette-t", label: "Brunette T", body: "F" as const, url: "https://raw.githubusercontent.com/met4citizen/TalkingHead/main/avatars/brunette-t.glb" },
  { id: "avaturn", label: "Avaturn", body: "M" as const, url: "https://raw.githubusercontent.com/met4citizen/TalkingHead/main/avatars/avaturn.glb" },
  { id: "avatarsdk", label: "AvatarSDK", body: "M" as const, url: "https://raw.githubusercontent.com/met4citizen/TalkingHead/main/avatars/avatarsdk.glb" },
  { id: "mpfb", label: "MPFB", body: "M" as const, url: "https://raw.githubusercontent.com/met4citizen/TalkingHead/main/avatars/mpfb.glb" },
  { id: "vroid", label: "VRoid", body: "F" as const, url: "https://raw.githubusercontent.com/met4citizen/TalkingHead/main/avatars/vroid.glb" },
];

const HEADTTS_WORKER =
  "https://cdn.jsdelivr.net/npm/@met4citizen/headtts@1.3/modules/worker-tts.mjs";
const HEADTTS_DICT =
  "https://cdn.jsdelivr.net/npm/@met4citizen/headtts@1.3/dictionaries/";

const TEST_SCRIPT =
  "Hey Erez, this is Atlas. I'm running in your browser through TalkingHead, with Kokoro neural speech and viseme lip-sync from HeadTTS. No server. No per-minute cost. Try the mood buttons below to switch my expression.";

const MOODS = [
  { name: "neutral", label: "Neutral", icon: Smile },
  { name: "happy", label: "Happy", icon: Heart },
  { name: "sad", label: "Sad", icon: Frown },
  { name: "angry", label: "Angry", icon: Angry },
  { name: "fear", label: "Fear", icon: ShieldAlert },
  { name: "love", label: "Love", icon: Heart },
] as const;

function HeroPanel() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<TalkingHead | null>(null);
  const ttsRef = useRef<HeadTTS | null>(null);

  const [loadState, setLoadState] = useState<
    "idle" | "loading-avatar" | "loaded" | "load-error"
  >("idle");
  const [loadPct, setLoadPct] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [ttsState, setTtsState] = useState<
    "cold" | "warming" | "ready" | "speaking" | "tts-error"
  >("cold");
  const [ttsError, setTtsError] = useState<string | null>(null);

  const [activeMood, setActiveMood] = useState<string>("neutral");
  const [activeAvatarId, setActiveAvatarId] = useState<string>(AVATARS[0].id);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    let cancelled = false;
    setLoadState("loading-avatar");
    setLoadPct(0);

    const head = new TalkingHead(node, {
      lipsyncModules: [],
      cameraView: "upper",
      modelRoot: "Armature",
      modelMovementFactor: 0.4,
      cameraRotateEnable: true,
      cameraPanEnable: false,
      cameraZoomEnable: true,
      lightAmbientIntensity: 2,
      lightDirectIntensity: 30,
    });
    (head as unknown as { lipsync: Record<string, unknown> }).lipsync.en = new LipsyncEn();
    headRef.current = head;

    const initialAvatar = AVATARS[0];
    head
      .showAvatar(
        { url: initialAvatar.url, body: initialAvatar.body, avatarMood: "neutral", lipsyncLang: "en" },
        (ev: ProgressEvent) => {
          if (ev.lengthComputable) {
            setLoadPct(Math.min(100, Math.round((ev.loaded / ev.total) * 100)));
          }
        },
      )
      .then(() => { if (!cancelled) setLoadState("loaded"); })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setLoadError(msg);
        setLoadState("load-error");
      });

    return () => {
      cancelled = true;
      try { (head as unknown as { stop?: () => void }).stop?.(); } catch { /* ignore */ }
      headRef.current = null;
    };
  }, []);

  const warmUpTTS = useCallback(async () => {
    if (ttsRef.current || ttsState === "warming") return;
    setTtsState("warming");
    setTtsError(null);
    try {
      const tts = new HeadTTS({
        endpoints: ["webgpu", "wasm"],
        languages: ["en-us"],
        voices: ["af_bella"],
        defaultVoice: "af_bella",
        defaultLanguage: "en-us",
        workerModule: HEADTTS_WORKER,
        dictionaryURL: HEADTTS_DICT,
      });
      await tts.connect();
      tts.onmessage = () => { /* swallow */ };
      await tts.synthesize({ input: "Ready." });
      ttsRef.current = tts;
      setTtsState("ready");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTtsError(msg);
      setTtsState("tts-error");
    }
  }, [ttsState]);

  const speakWithKokoro = useCallback(async (text: string) => {
    const head = headRef.current;
    const tts = ttsRef.current;
    if (!head || !tts) return;
    setTtsState("speaking");
    try {
      tts.onmessage = (msg: { data?: SpeechChunk } & Record<string, unknown>) => {
        const d = msg.data;
        if (!d) return;
        head.speakAudio(d, { lipsyncLang: "en" });
      };
      await tts.synthesize({ input: text, voice: "af_bella" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTtsError(msg);
    } finally {
      setTimeout(() => setTtsState("ready"), 500);
    }
  }, []);

  const switchAvatar = useCallback(async (avatarId: string) => {
    const head = headRef.current;
    if (!head) return;
    const avatar = AVATARS.find((a) => a.id === avatarId);
    if (!avatar) return;
    setActiveAvatarId(avatarId);
    setLoadState("loading-avatar");
    setLoadPct(0);
    try {
      await head.showAvatar(
        { url: avatar.url, body: avatar.body, avatarMood: activeMood, lipsyncLang: "en" },
        (ev: ProgressEvent) => {
          if (ev.lengthComputable) {
            setLoadPct(Math.min(100, Math.round((ev.loaded / ev.total) * 100)));
          }
        },
      );
      setLoadState("loaded");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setLoadError(msg);
      setLoadState("load-error");
    }
  }, [activeMood]);

  const setMood = useCallback((name: string) => {
    const head = headRef.current;
    if (!head) return;
    try { head.setMood(name); setActiveMood(name); }
    catch (err) { console.warn("setMood failed:", err); }
  }, []);

  return (
    <div className="rounded-2xl border bg-white dark:bg-stone-900 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b">
        <div>
          <h2 className="text-base font-semibold">TalkingHead + HeadTTS (Kokoro)</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Photoreal-ish RPM character · 6 avatars · mood expressions · MIT
          </p>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wide rounded bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300 px-2 py-1">
          MIT · BROWSER
        </span>
      </div>

      <div className="relative w-full h-[520px] bg-stone-900">
        <div ref={containerRef} className="absolute inset-0" />
        {loadState !== "loaded" ? (
          <div className="absolute inset-0 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm">
            <div className="text-center">
              {loadState === "load-error" ? (
                <p className="text-xs text-amber-300 font-mono">{loadError}</p>
              ) : (
                <>
                  <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-stone-400" />
                  <p className="text-sm text-stone-300">Loading avatar {loadPct ? `${loadPct}%` : "…"}</p>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {ttsState === "cold" ? (
            <button onClick={warmUpTTS} disabled={loadState !== "loaded"}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium disabled:opacity-50">
              <Volume2 className="w-4 h-4" /> Wake up Kokoro (~80 MB)
            </button>
          ) : ttsState === "warming" ? (
            <button disabled className="flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600/60 text-white text-sm font-medium cursor-not-allowed">
              <Loader2 className="w-4 h-4 animate-spin" /> Warming Kokoro…
            </button>
          ) : ttsState === "tts-error" ? (
            <span className="text-xs text-amber-300 font-mono">Kokoro: {ttsError}</span>
          ) : (
            <button onClick={() => speakWithKokoro(TEST_SCRIPT)} disabled={ttsState === "speaking"}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-700 text-white hover:bg-green-800 text-sm font-medium disabled:opacity-60">
              {ttsState === "speaking" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4" />}
              Speak with Kokoro
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-xs text-muted-foreground mr-1">Character:</span>
          {AVATARS.map((a) => (
            <button key={a.id} onClick={() => switchAvatar(a.id)} disabled={loadState === "loading-avatar"}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors disabled:opacity-50 ${activeAvatarId === a.id ? "bg-blue-600 text-white border-blue-600" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-100"}`}
              title={a.body === "M" ? "Male body" : "Female body"}>
              <span className={`font-mono text-[10px] ${activeAvatarId === a.id ? "opacity-90" : "opacity-50"}`}>{a.body}</span>
              {a.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="text-xs text-muted-foreground mr-1">Mood:</span>
          {MOODS.map(({ name, label, icon: Icon }) => (
            <button key={name} onClick={() => setMood(name)} disabled={loadState !== "loaded"}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition-colors disabled:opacity-50 ${activeMood === name ? "bg-stone-700 text-white border-stone-700" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-100"}`}>
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground pt-1 leading-relaxed">
          6 Ready Player Me avatars (mix of M/F), hot-linked from the TalkingHead repo (MIT). Kokoro voice: af_bella.
        </p>
      </div>
    </div>
  );
}

type SpeechChunk = {
  audio?: ArrayBuffer | Uint8Array;
  words?: string[];
  wtimes?: number[];
  wdurations?: number[];
  visemes?: string[];
  vtimes?: number[];
  vdurations?: number[];
};

function VendorCard({ badge, title, subtitle, pitch, bullets, cta, href, apiKeyEnvName, status }: {
  badge: string; title: string; subtitle: string; pitch: string; bullets: string[]; cta: string; href: string; apiKeyEnvName: string; status: "unconfigured" | "configured";
}) {
  return (
    <div className="rounded-2xl border bg-white dark:bg-stone-900 p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <span className="shrink-0 text-[10px] font-mono uppercase tracking-wide rounded bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 px-2 py-1">{badge}</span>
      </div>
      <div className="flex-1 space-y-3">
        <p className="text-sm">{pitch}</p>
        <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
          {bullets.map((b) => (<li key={b}>{b}</li>))}
        </ul>
      </div>
      <div className="mt-4 pt-3 border-t space-y-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="w-3.5 h-3.5" />
          <code className="text-[10px]">{apiKeyEnvName}</code>
          <span>{status === "configured" ? "✓ configured" : "not set"}</span>
        </div>
        <a href={href} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-md bg-stone-700 text-white hover:bg-stone-800 text-sm font-medium">
          {cta} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

function MetaHumanDeferCard() {
  return (
    <div className="rounded-2xl border bg-stone-50 dark:bg-stone-950 p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold">Unreal MetaHuman + Pixel Streaming</h3>
          <p className="text-xs text-muted-foreground">Hollywood-grade · server-rendered</p>
        </div>
        <span className="shrink-0 text-[10px] font-mono uppercase tracking-wide rounded bg-stone-200 dark:bg-stone-800 text-stone-600 dark:text-stone-300 px-2 py-1">DEFERRED</span>
      </div>
      <div className="flex-1 space-y-3 text-sm">
        <p>Engine + Pixel Streaming infra are open source. The blocker is the MetaHuman EULA which forbids hosted/SaaS/multi-tenant delivery. Personal/internal use only.</p>
        <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
          <li>Self-host on Vast.ai RTX 4090: ~$13/mo per CCU</li>
          <li>Setup: 19-28 hours first-time</li>
          <li>Cannot ship to tenants — Epic EULA blocker</li>
        </ul>
      </div>
    </div>
  );
}

export function AvatarsPage() {
  return (
    <div className="max-w-7xl px-6 pb-12 pt-4">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-stone-500/15">
            <Sparkles className="w-5 h-5 text-stone-600 dark:text-stone-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Avatars Lab</h1>
            <p className="text-sm text-muted-foreground">
              Three browser-native OSS hero panels side-by-side: photoreal-ish RPM (TalkingHead), anime-stylized (VRoid), polished CC0 robot (RobotExpressive). All three run Kokoro neural TTS in the browser. <a href="/kb-doc/research/3d-avatar-libraries" className="underline hover:no-underline">Research backing →</a>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <HeroPanel />
        <VRoidPanel />
        <RobotPanel />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <VendorCard badge="WINNER · 2026" title="Tavus CVI (Phoenix-3)" subtitle="Near-photoreal · sub-1s · WebRTC"
          pitch="Won the 2026 Avatar Arena 4/4 head-to-heads. Sub-1-second latency, 30+ languages, browser SDK via Daily. Free 25 min, then $59-$397/mo + usage."
          bullets={["Phoenix-3 model + Sparrow-0 LLM", "Photoreal feel", "React SDK, 1-day deploy"]}
          cta="Sign up at tavus.io (25 min free)" href="https://tavus.io" apiKeyEnvName="VITE_TAVUS_API_KEY" status="unconfigured" />
        <VendorCard badge="CHEAPEST" title="Simli (Trinity)" subtitle="$0.05/min · <300 ms latency"
          pitch="Cheapest real-time photoreal API. $10 free credits. Trinity at 25 fps."
          bullets={["$0.05/min", "Sub-300ms", "JS SDK + Pipecat plugin"]}
          cta="Sign up at simli.com ($10 free credit)" href="https://www.simli.com" apiKeyEnvName="VITE_SIMLI_API_KEY" status="unconfigured" />
        <MetaHumanDeferCard />
      </div>

      <div className="mt-6 rounded-2xl border bg-stone-50 dark:bg-stone-950 p-5">
        <div className="flex items-start gap-3">
          <Cpu className="w-5 h-5 mt-0.5 text-stone-500 shrink-0" />
          <div className="text-xs text-muted-foreground space-y-1">
            <p><strong className="text-foreground">Notable shutdowns:</strong> Ready Player Me sunset Jan 31, 2026. Soul Machines in receivership Feb 2026.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

(AvatarsPage as unknown as { path: string }).path = "/avatars";
